/**
 * GA4 module singleton — a thin convenience over `createGoogleAnalyticsProvider`
 * for the common SPA case: initialize once at startup, then record a page_view
 * on each client-side route change.
 *
 * Caller owns the measurement-ID policy (e.g. "prod default, dev off"):
 *
 * ```ts
 * import { initGA4, trackPageview } from '@gjl/analytics';
 *
 * const GA_ID =
 *   (import.meta.env.VITE_GA_ID as string | undefined) ||
 *   (import.meta.env.PROD ? 'G-XXXXXXXXXX' : undefined);
 *
 * initGA4(GA_ID);                 // no-op when GA_ID is falsy
 * // in a router effect:
 * trackPageview(location.pathname + location.search);
 * ```
 */

import { createGoogleAnalyticsProvider } from './providers/google-analytics'
import type { AnalyticsProvider } from './types'

let provider: AnalyticsProvider | null = null

/**
 * Hosts that must never report, whatever id a bundle was built with.
 *
 * WHY A HOSTNAME CHECK AND NOT JUST BUILD CONFIG (2026-09-05). Measured on
 * property 527738893 over 60 days: `localhost` 21 sessions / 97 views and
 * `dev.fuguelab.com` 19 / 72, mixed into 1,139 real sessions. The localhost
 * half is CI: `scripts/hydration-smoke.mjs --require-browser` boots the built
 * `dist/` in headless Chrome, and because the prerender runs in production mode
 * the measurement id is already baked into the bundle it loads. So every PR
 * that touches a site fires real hits from a runner.
 *
 * Build config alone cannot fix that half — the artifact CI exercises is, by
 * design, the production artifact. The host it runs on is the only thing that
 * distinguishes the rehearsal from the performance.
 *
 * `.local`/`.test` are here for the same reason a Vite `--host` preview on a LAN
 * name is still not a visitor.
 */
const NON_REPORTING_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0'])

function isNonReportingHost(): boolean {
  const host = window.location?.hostname ?? ''
  return NON_REPORTING_HOSTS.has(host) || host.endsWith('.local') || host.endsWith('.test')
}

/**
 * Initialize GA4 as a module singleton. No-op when `measurementId` is falsy
 * (so dev/un-provisioned builds stay clean), off the browser, or on a
 * non-reporting host. Idempotent.
 */
export function initGA4(measurementId: string | undefined | null): void {
  if (provider || !measurementId || typeof window === 'undefined') return
  if (isNonReportingHost()) return
  provider = createGoogleAnalyticsProvider({ measurementId })
}

/**
 * Record a SPA page view for the given path. No-op until `initGA4` has run with
 * a valid measurement ID.
 */
export function trackPageview(path: string): void {
  if (!provider || typeof document === 'undefined') return
  provider.page(document.title, { path })
}

/** True once `initGA4` has been called with a valid measurement ID. */
export function analyticsEnabled(): boolean {
  return provider !== null
}
